// ST-Operations-os#86 — GET /api/v1/auth/status: só 200 é VEREDITO. 429, 5xx,
// timeout e rede são TRANSPORTE (`transient`, com Retry-After), nunca "pendente".
type Opt = string | null | undefined
export type AuthStatusBody = { status: string; role?: Opt; tenantId?: Opt; languageEffective?: Opt }
export type AuthStatusResult =
  | { kind: 'ok'; body: AuthStatusBody }
  | { kind: 'rejected'; status: 401 | 403 }
  | { kind: 'not_found' }
  | { kind: 'transient'; status?: number; retryAfterMs: number | null; error?: unknown }
export type TransientResult = Extract<AuthStatusResult, { kind: 'transient' }>

/** Retry-After maior que isto encerra o ciclo e vira espera em segundo plano. */
export const MAX_INLINE_WAIT_MS = 10_000

export function parseRetryAfterMs(header: string | null | undefined, nowMs: number): number | null {
  const raw = (header ?? '').trim()
  if (raw === '') return null
  if (/^\d+$/.test(raw)) return Number(raw) * 1_000
  const at = Date.parse(raw)
  return Number.isNaN(at) ? null : Math.max(0, at - nowMs)
}

/** Jitter só para CIMA (Retry-After é piso). */
function withJitter(ms: number, spread: number, random: number): number {
  return Math.round(ms * (1 + random * spread))
}

export function inlineWaitMs(attempt: number, last: TransientResult, random: number): number {
  if (last.retryAfterMs !== null) return withJitter(last.retryAfterMs, 0.1, random)
  return withJitter(attempt === 2 ? 1_000 : 2_000, 0.5, random)
}

export function backgroundWaitMs(
  cycles: number,
  last: TransientResult | null,
  random: number,
): number {
  const backoff = Math.min(15_000 * 2 ** (cycles - 1), 60_000)
  return withJitter(Math.max(backoff, last?.retryAfterMs ?? 0), 0.2, random)
}

type ResponseLike = Pick<Response, 'ok' | 'status' | 'json'> & { headers?: Pick<Headers, 'get'> }

export async function classifyAuthStatusResponse(
  res: ResponseLike,
  nowMs: number,
): Promise<AuthStatusResult> {
  if (res.ok) return { kind: 'ok', body: (await res.json()) as AuthStatusBody }
  if (res.status === 401 || res.status === 403) return { kind: 'rejected', status: res.status }
  if (res.status === 404) return { kind: 'not_found' }
  const retryAfterMs = parseRetryAfterMs(res.headers?.get('Retry-After') ?? null, nowMs)
  return { kind: 'transient', status: res.status, retryAfterMs }
}

type Params = { baseUrl: string; token: string; tenantId: string; userId: string }

export async function fetchAuthStatus(p: Params): Promise<AuthStatusResult> {
  const headers = {
    Authorization: `Bearer ${p.token}`,
    'X-Tenant-Id': p.tenantId,
    'X-User-Id': p.userId,
  }
  try {
    const signal = AbortSignal.timeout(10_000)
    const res = await fetch(`${p.baseUrl}/api/v1/auth/status`, { headers, signal })
    return await classifyAuthStatusResponse(res, Date.now())
  } catch (error) {
    // Rede caiu, timeout ou corpo ilegível: transporte, não veredito.
    return { kind: 'transient', retryAfterMs: null, error }
  }
}
